/**
 * The RUNTIME half of "which identity provider does this browser sign in
 * against" (US-011).
 *
 * <h2>Why this module exists</h2>
 *
 * Before US-011 the auth adapter was chosen at BUILD time, from
 * `VITE_AUTH_PROVIDER`. That made the cutover switch a rebuild-and-redeploy,
 * which is not independently reversible: rolling the backend back left a bundle
 * still talking to the wrong issuer until a new one shipped, and rolling the
 * bundle back meant a build. US-011 AC-4 requires every switch input to be
 * explicit, fail-closed and independently reversible, so the answer now comes
 * from `GET /api/config/public` — the same runtime source `auth_provider`
 * already came from — and the build-time flag survives only as the boot
 * fallback for the window before that response lands.
 *
 * <h2>Why it holds no imports</h2>
 *
 * It is imported by `authProvider.ts` (which pulls in both vendor adapters) and
 * by `api/publicConfig.ts` (which parses the response). If this module imported
 * either of them the graph would be circular, and — worse — `publicConfig.ts`
 * would drag `oidc-client-ts` into every bundle that merely wanted the Stripe
 * key. So the provider-name type and its normaliser live HERE, at the bottom of
 * the graph, and `authProvider.ts` re-exports them for the call sites that
 * already import from there.
 *
 * <h2>Fail-safe, in one direction only</h2>
 *
 * Every read below degrades to keycloak: an unset snapshot, an unrecognised
 * provider name, a fetch that never landed, a config missing the fields an
 * authorization request needs. That direction is not arbitrary. Answering
 * "scalekit" when the deployment is not ready sends every visitor to an
 * authorization request that fails and nobody can sign in; answering "keycloak"
 * when the deployment HAS moved means the cutover has not taken effect in this
 * tab yet, which a reload fixes. One is a lockout, the other is a delay.
 */

export type AuthProviderName = 'keycloak' | 'scalekit';

/** The provider that runs when nothing recognisable says otherwise. */
export const DEFAULT_AUTH_PROVIDER: AuthProviderName = 'keycloak';

const KNOWN_PROVIDERS: readonly AuthProviderName[] = ['keycloak', 'scalekit'];

/**
 * Normalise ANY untrusted provider name — the build-time env flag, the
 * `auth_provider` field, the `login_provider` field — failing SAFE. An unknown
 * value is a misconfiguration; answering it with "then nobody can log in" would
 * take the dashboard down on a typo, so it degrades to the rollback provider
 * instead.
 *
 * Every reader shares this one function on purpose: a runtime value that
 * normalised differently from the build-time value would let the two disagree
 * about a typo, which is precisely the case where they must not.
 */
export const normaliseAuthProviderName = (raw: unknown): AuthProviderName => {
  const normalised = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return (KNOWN_PROVIDERS as readonly string[]).includes(normalised)
    ? (normalised as AuthProviderName)
    : DEFAULT_AUTH_PROVIDER;
};

/**
 * The non-secret browser client the backend serves when — and only when — this
 * deployment advertises Scalekit login. Mirrors the `VITE_SCALEKIT_*` names so
 * a runtime value and a build-time one describe the same thing.
 *
 * Every field except `issuer` and `clientId` is optional, and an absent one
 * means "use the adapter's own default". The adapter is where each endpoint's
 * ATTESTED / NOT-ATTESTED evidence lives (`src/auth/scalekit.ts`), so keeping
 * the derivation there means the unverified pair carries exactly one marker.
 */
export type ScalekitBrowserConfig = {
  issuer: string;
  clientId: string;
  /** The non-secret tenant selector, identical to the backend's required oid. */
  organizationId: string;
  authorizeEndpoint?: string;
  tokenEndpoint?: string;
  jwksEndpoint?: string;
  endSessionEndpoint?: string;
  scope?: string;
  googleHint?: string;
  googleParam?: string;
};

export type RuntimeAuthConfig = {
  /** Which adapter the backend says this browser must run. */
  loginProvider: AuthProviderName;
  /** The Scalekit client, or null when the SPA must not build one. */
  scalekit: ScalekitBrowserConfig | null;
};

let snapshot: RuntimeAuthConfig | null = null;

/**
 * Bumped on every adopt. `scalekit.ts` memoises a `UserManager` built from the
 * config that was current at the time; comparing generations is how it notices
 * that the answer changed underneath it and rebuilds, rather than serving a
 * client pointed at the boot-time defaults for the rest of the page's life.
 */
let generation = 0;

export const runtimeAuthConfigGeneration = (): number => generation;

/**
 * Adopt a config the backend served.
 *
 * <p>REFUSES to adopt `scalekit` without a usable client. The backend already
 * degrades in this case, so this is defence in depth against an older or
 * hand-rolled response — and the cost of getting it wrong is a lockout, which
 * is exactly the class of failure worth checking twice.
 */
export const adoptRuntimeAuthConfig = (config: RuntimeAuthConfig): void => {
  const usable =
    config.loginProvider === 'scalekit' &&
    !!config.scalekit &&
    config.scalekit.issuer.trim().length > 0 &&
    config.scalekit.clientId.trim().length > 0 &&
    config.scalekit.organizationId.trim().startsWith('org_') &&
    config.scalekit.organizationId.trim().length > 4;
  snapshot = usable
    ? { loginProvider: 'scalekit', scalekit: config.scalekit }
    : { loginProvider: DEFAULT_AUTH_PROVIDER, scalekit: null };
  generation += 1;
};

/** The adopted config, or null when none has landed yet. */
export const getRuntimeAuthConfig = (): RuntimeAuthConfig | null => snapshot;

/** The adopted Scalekit client, or null. Never a partially-configured one. */
export const getRuntimeScalekitConfig = (): ScalekitBrowserConfig | null =>
  snapshot?.scalekit ?? null;

/** Test-only: drop the snapshot. Production code must never call this. */
export const __resetRuntimeAuthConfig = (): void => {
  snapshot = null;
  generation += 1;
};
